import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Row, Col, Card } from 'react-bootstrap';
import axios from 'axios';

const ApprovalTrackingChart = () => {
    const [approvalStatusData, setApprovalStatusData] = useState([]);
    const [moduleComparisonData, setModuleComparisonData] = useState([]);

    useEffect(() => {
        const fetchApprovalData = async () => {
            try {
                const response = await axios.get('https://api.admin-panel.nhcholding.com/api/approval-tracking', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });

                if (response.data && response.data.status) {
                    const trackingData = response.data.data;

                    // Calculate overall counts
                    const approved = trackingData.filter((item) => item.current_status === 'approved').length;
                    const pending = trackingData.filter((item) => item.current_status === 'pending').length;
                    const rejected = trackingData.filter((item) => item.current_status === 'rejected').length;
                    const managerApproved = trackingData.filter((item) => item.current_status === 'manager_approved').length;
                    const needsRevision = trackingData.filter((item) => item.current_status === 'rejected_for_edit').length;

                    // Prepare data for the Approval Status Pie Chart
                    setApprovalStatusData([
                        { name: 'Approved', value: approved, color: '#28a745' },
                        { name: 'Pending', value: pending, color: '#ffc107' },
                        { name: 'Rejected', value: rejected, color: '#dc3545' },
                        { name: 'Manager Approved', value: managerApproved, color: '#007bff' },
                        { name: 'Needs Revision', value: needsRevision, color: '#17a2b8' },
                    ]);

                    // Calculate data by module for comparison
                    const moduleStats = {};
                    
                    trackingData.forEach(item => {
                        const module = item.module;
                        if (!moduleStats[module]) {
                            moduleStats[module] = {
                                module: module,
                                approved: 0,
                                pending: 0,
                                rejected: 0,
                                manager_approved: 0,
                                rejected_for_edit: 0,
                                total: 0
                            };
                        }
                        
                        moduleStats[module][item.current_status]++;
                        moduleStats[module].total++;
                    });

                    // Convert to array format for chart
                    const moduleComparisonArray = Object.values(moduleStats).map(stat => ({
                        module: stat.module.replace(' Management', ''), // Shorten names for chart
                        Approved: stat.approved,
                        Pending: stat.pending,
                        Rejected: stat.rejected,
                        'Manager Approved': stat.manager_approved,
                        'Needs Revision': stat.rejected_for_edit,
                        Total: stat.total
                    }));

                    setModuleComparisonData(moduleComparisonArray);
                } else {
                    console.error('Unexpected response format:', response.data);
                }
            } catch (error) {
                console.error('Error fetching approval tracking data:', error);
            }
        };

        fetchApprovalData();
    }, []);

    const COLORS = {
        'Approved': '#28a745',
        'Pending': '#ffc107', 
        'Rejected': '#dc3545',
        'Manager Approved': '#007bff',
        'Needs Revision': '#17a2b8'
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border rounded shadow">
                    <p className="mb-1 fw-bold">{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="mb-1" style={{ color: entry.color }}>
                            {`${entry.dataKey}: ${entry.value}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const PieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            return (
                <div className="bg-white p-2 border rounded shadow">
                    <p className="mb-1 fw-bold">{data.name}</p>
                    <p className="mb-0" style={{ color: data.payload.color }}>
                        Count: {data.value}
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <Row>
            <Col md={6}>
                <Card className="h-100">
                    <Card.Header>
                        <h5 className="mb-0">Overall Approval Status Breakdown</h5>
                    </Card.Header>
                    <Card.Body className="d-flex align-items-center justify-content-center">
                        <PieChart width={400} height={350}>
                            <Pie
                                data={approvalStatusData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={120}
                                label={({ name, value }) => `${name}: ${value}`}
                                labelLine={false}
                            >
                                {approvalStatusData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<PieTooltip />} />
                            <Legend />
                        </PieChart>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card className="h-100">
                    <Card.Header>
                        <h5 className="mb-0">Module Comparison</h5>
                    </Card.Header>
                    <Card.Body>
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart
                                data={moduleComparisonData}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="module" />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey="Approved" stackId="a" fill={COLORS['Approved']} />
                                <Bar dataKey="Manager Approved" stackId="a" fill={COLORS['Manager Approved']} />
                                <Bar dataKey="Pending" stackId="a" fill={COLORS['Pending']} />
                                <Bar dataKey="Needs Revision" stackId="a" fill={COLORS['Needs Revision']} />
                                <Bar dataKey="Rejected" stackId="a" fill={COLORS['Rejected']} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default ApprovalTrackingChart;
